import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  CreditCard, CheckCircle2, Loader2, Sparkles, Calendar, Crown, Zap,
} from "lucide-react";
import { toast } from "sonner";

const PLAN_FEATURES = {
  Starter: ["Performance essencial", "Objetivos & pontos", "Ranking de equipa", "3 módulos de auto-educação"],
  Growth: ["Tudo do Starter", "Academia completa + IA", "Onboarding guiado", "Coaching & perfis", "Suporte prioritário"],
};

export default function Billing() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [pkgs, setPkgs] = useState(null);
  const [interval, setInterval] = useState("annual");
  const [busy, setBusy] = useState("");
  const [checking, setChecking] = useState(false);

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/billing/subscription"), api.get("/billing/packages")]);
    setSub(s.data.subscription); setPkgs(p.data);
  };

  useEffect(() => {
    load();
    const sid = params.get("session_id");
    if (sid) poll(sid, 0);
    // eslint-disable-next-line
  }, []);

  const poll = async (sid, attempt) => {
    setChecking(true);
    if (attempt >= 6) { setChecking(false); toast.message("A confirmar o pagamento... atualize em instantes."); return; }
    try {
      const { data } = await api.get(`/billing/status/${sid}`);
      if (data.payment_status === "paid") {
        toast.success("Subscrição ativada! 🎉");
        setParams({}, { replace: true });
        setChecking(false);
        await load();
        return;
      }
      if (data.status === "expired") { setChecking(false); toast.error("Sessão de pagamento expirada."); setParams({}, { replace: true }); return; }
    } catch (e) {}
    setTimeout(() => poll(sid, attempt + 1), 2000);
  };

  const subscribe = async (planKey) => {
    const package_id = `${planKey.toLowerCase()}_${interval}`;
    setBusy(package_id);
    try {
      const { data } = await api.post("/billing/checkout", { package_id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao iniciar pagamento");
      setBusy("");
    }
  };

  if (!pkgs) return <div className="h-96 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-blue" /></div>;

  const priceFor = (planKey) => pkgs[`${planKey.toLowerCase()}_${interval}`]?.amount ?? 0;
  const monthlyEquivalent = (planKey) =>
    interval === "annual" ? Math.round(priceFor(planKey) / 12) : priceFor(planKey);
  const annualSaving = (planKey) => {
    const m = pkgs[`${planKey.toLowerCase()}_monthly`]?.amount ?? 0;
    const a = pkgs[`${planKey.toLowerCase()}_annual`]?.amount ?? 0;
    return Math.round(m * 12 - a);
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2">
          <CreditCard className="text-brand-blue" /> Faturação
        </h1>
        <p className="text-brand-muted text-sm mt-1">Gere a subscrição da tua agência. Poupa 20% no plano anual.</p>
      </div>

      {checking && (
        <div className="p-4 rounded-sm bg-brand-blue/10 border border-brand-blue/30 flex items-center gap-3" data-testid="billing-checking">
          <Loader2 className="h-4 w-4 animate-spin text-brand-blue" /> <span className="text-sm">A confirmar o teu pagamento...</span>
        </div>
      )}

      {/* Current subscription */}
      <div className="p-6 rounded-sm bg-brand-surface border border-brand-border" data-testid="current-subscription">
        <h2 className="font-heading font-semibold mb-3">Subscrição atual</h2>
        {sub && sub.status === "active" ? (
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-lg font-heading font-bold text-white">{sub.plan}</span>
            <span className="text-xs px-2 py-1 rounded-sm bg-brand-success/15 text-brand-success font-medium">Ativa</span>
            <span className="text-sm text-brand-muted">{sub.interval === "annual" ? "Anual" : "Mensal"}</span>
            <span className="text-sm text-brand-muted flex items-center gap-1">
              <Calendar size={14} /> Válida até {new Date(sub.valid_until).toLocaleDateString("pt-PT")}
            </span>
          </div>
        ) : (
          <p className="text-sm text-brand-muted">Sem subscrição ativa. Escolhe um plano abaixo.</p>
        )}
      </div>

      {/* Interval toggle */}
      <div className="flex items-center gap-3">
        <div className="inline-flex rounded-sm border border-brand-border overflow-hidden" data-testid="billing-interval">
          {[["monthly", "Mensal"], ["annual", "Anual"]].map(([v, l]) => (
            <button key={v} onClick={() => setInterval(v)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${interval === v ? "bg-brand-blue text-white" : "text-brand-muted hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>
        {interval === "annual" && (
          <span className="text-xs px-2 py-1 rounded-sm bg-brand-orange/15 text-brand-orange font-bold flex items-center gap-1">
            <Sparkles size={12} /> Poupa 20%
          </span>
        )}
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-3 gap-5 items-start">
        {["Starter", "Growth"].map((planKey) => {
          const featured = planKey === "Growth";
          const Icon = featured ? Crown : Zap;
          return (
            <div key={planKey} data-testid={`plan-${planKey.toLowerCase()}`}
              className={`p-7 rounded-sm border ${featured ? "border-brand-orange bg-brand-orange/5 glow-orange" : "border-brand-border bg-brand-surface"}`}>
              <div className="flex items-center gap-2">
                <Icon size={18} className={featured ? "text-brand-orange" : "text-brand-blue"} />
                <h3 className="font-heading font-bold text-xl">{planKey}</h3>
              </div>
              <p className="mt-4">
                <span className="font-heading font-black text-3xl">€{monthlyEquivalent(planKey)}</span>
                <span className="text-brand-muted text-sm">/mês</span>
              </p>
              {interval === "annual" && (
                <p className="text-xs text-brand-muted mt-1">
                  €{priceFor(planKey)} faturado anualmente · <span className="text-brand-success font-semibold">poupa €{annualSaving(planKey)}</span>
                </p>
              )}
              <ul className="mt-6 space-y-2.5">
                {PLAN_FEATURES[planKey].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 size={16} className="text-brand-success mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={() => subscribe(planKey)} disabled={!!busy} data-testid={`subscribe-${planKey.toLowerCase()}`}
                className={`w-full mt-7 rounded-sm font-bold ${featured ? "bg-brand-orange hover:bg-brand-orangehover" : "bg-brand-blue hover:bg-brand-bluehover"}`}>
                {busy === `${planKey.toLowerCase()}_${interval}` ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscrever"}
              </Button>
            </div>
          );
        })}
        <div className="p-7 rounded-sm border border-brand-border bg-brand-surface">
          <h3 className="font-heading font-bold text-xl">Elite</h3>
          <p className="mt-4 font-heading font-black text-2xl">Sob consulta</p>
          <p className="text-xs text-brand-muted mt-1">Redes & multi-agência</p>
          <ul className="mt-6 space-y-2.5 text-sm">
            {["Tudo do Growth", "CRM multi-agência", "Automatismos à medida", "Gestor de sucesso dedicado"].map((f) => (
              <li key={f} className="flex items-start gap-2"><CheckCircle2 size={16} className="text-brand-success mt-0.5 shrink-0" /> {f}</li>
            ))}
          </ul>
          <Button variant="outline" className="w-full mt-7 border-brand-border rounded-sm font-semibold"
            onClick={() => toast.message("Contacte comercial@alliance4growth.com")}>Contacte-nos</Button>
        </div>
      </div>

      <p className="text-xs text-brand-muted">Pagamentos processados de forma segura via Stripe. Modo de teste — use o cartão 4242 4242 4242 4242.</p>
    </div>
  );
}
